import { Pipe, PipeTransform } from '@angular/core';
import { ValidationErrors } from '@angular/forms';

interface ValidationErrorMessages {
  // For default validators
  required: string;
  maxlength: string;
  minlength: string;
  max: string;
  min: string;
  email: string;
  pattern: string;

  // For custom validators
  phoneNumber: string;
  address: string;
  isUnique: string;
  areaCodes: string;
  zipCodes: string;
  pharmacyIds: string;
  autoWriteoffAdjType: string;
}

function getAddressErrorMessage(errors: ValidationErrors | null): string {
  if (errors?.address != null) {
    const { currentAddress, correctAddress } = errors.address;
    if (currentAddress.addressOne !== correctAddress.addressOne) {
      return `Line 1 ${currentAddress.addressOne} seems invalid.`;
    } else if (currentAddress.addressTwo !== correctAddress.addressTwo) {
      return `Line 2 ${currentAddress.addressTwo} seems invalid.`;
    } else if (currentAddress.city !== correctAddress.city) {
      return `City ${currentAddress.city} seems invalid.`;
    } else if (currentAddress.state !== correctAddress.state) {
      return `State ${currentAddress.state} seems invalid.`;
    } else if (currentAddress.zip !== correctAddress.zip) {
      return `ZIP ${currentAddress.zip} seems invalid.`;
    }
  }
  return '';
}

function getIsUniqueErrorMessage(errors: ValidationErrors | null, formControlName: string): string {
  if (errors?.isUnique != null) {
    const formControlNameLowerCased = formControlName.toLowerCase();
    return `An item with this ${formControlNameLowerCased} already exists, each item must have a unique ${formControlNameLowerCased}`;
  }
  return '';
}

function getAreaCodesErrorMessage(error: ValidationErrors | null): string {
  if (error?.areaCodes) {
    return `${error.areaCodes} is an invalid Area Code`;
  }
  return '';
}

function getZipCodesErrorMessage(error: ValidationErrors | null): string {
  if (error?.zipCodes) {
    return `${error.zipCodes} is an invalid ZIP Code`;
  }
  return '';
}

function getPharmacyIdsErrorMessage(error: ValidationErrors | null): string {
  if (error?.pharmacyIds) {
    return `${error.pharmacyIds} is an invalid Pharmacy ID`;
  }
  return '';
}

function getAutoWriteoffAdjTypeErrorMessage(error: ValidationErrors | null): string {
  if (error?.autoWriteoffAdjType?.name) {
    return `The Auto-Writeoff Credit Type (${error?.autoWriteoffAdjType?.name}) must be a Contractual Adjustment`;
  }
  return '';
}

@Pipe({
  name: 'fcListValidation',
})
export class FcListValidationPipe implements PipeTransform {
  public transform(
    errors: ValidationErrors | null,
    formControlName: string = 'This field',
    messageOverrides: Partial<ValidationErrorMessages> = {}
  ): string {
    if (errors == null) {
      return '';
    }

    const messages: ValidationErrorMessages = {
      // For default validators
      required: `${formControlName} is a required input`,
      maxlength: `${formControlName} must be ${errors?.maxlength?.requiredLength} characters or less`,
      minlength: `${formControlName} must be at least ${errors?.minlength?.requiredLength} characters`,
      max: `${formControlName} must be ${errors?.max?.max} or less`,
      min: `${formControlName} must be ${errors?.min?.min} or more`,
      email: 'Please use the following format: johndoe@yahoo.com',
      pattern: '', // We have no approve to display "Invalid input" for pattern error

      // For custom validators
      phoneNumber: `A phone number must have at least 7 digits or 10 or more digits`,
      address: getAddressErrorMessage(errors),
      isUnique: getIsUniqueErrorMessage(errors, formControlName),
      areaCodes: getAreaCodesErrorMessage(errors),
      zipCodes: getZipCodesErrorMessage(errors),
      pharmacyIds: getPharmacyIdsErrorMessage(errors),
      autoWriteoffAdjType: getAutoWriteoffAdjTypeErrorMessage(errors),

      // Override default error messages via custom error messages
      ...messageOverrides,
    };

    for (const errorName of Object.keys(errors)) {
      if (errorName in messages) {
        return messages[errorName as keyof ValidationErrorMessages];
      }
    }

    return 'Invalid input';
  }
}
